/**
 * Knowledge documents service — per-tenant custom knowledge migrated out of
 * Google Drive into `knowledge.documents` (metadata) + GCS (bodies).
 *
 * Metadata reads go straight to Supabase PostgREST (RLS-scoped). Body reads
 * and ALL writes go through the staff-gated ixadmin_api because each edit
 * pairs a GCS blob write with Supabase snapshot rows. Mirrors the auth
 * pattern of `services/websiteSync.ts`.
 */

import { logger } from '@inboundx/shared/platform';
import type { Database } from '@inboundx/shared/types';

import type { KnowledgeProgressOwner } from '@/domain/knowledgeOperations';
import {
    clearPendingRequestKey,
    pendingRequestKey,
    shouldRetainRequestKey,
} from '@/lib/requestIdempotency';
import { supabase } from '@/lib/supabase';

import {
    buildApiUrl,
    getAdminApiBaseUrl,
    getSupabaseAccessToken,
    parseErrorDetail,
    type ServiceErrorFactory,
} from './adminApi';

/**
 * Byte cap for one uploaded document, mirroring `MAX_UPLOAD_BYTES` on the admin
 * API. This is Firecrawl `/v2/parse`'s own ceiling: bigger files have to be
 * compressed before upload, since `/parse` accepts no URL.
 */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
export const MAX_TEXT_DOCUMENT_BYTES = 5 * 1024 * 1024;

type DocumentRow = Pick<
    Database['knowledge']['Tables']['documents']['Row'],
    | 'id'
    | 'site_domain'
    | 'path'
    | 'origin'
    | 'knowledge_type'
    | 'status'
    | 'title'
    | 'source_url'
    | 'tags'
    | 'current_snapshot_id'
    | 'created_at'
    | 'updated_at'
    | 'last_processed_at'
>;

export type KnowledgeReingestRunRow = Database['knowledge']['Tables']['reingest_runs']['Row'];

export type KnowledgeDocumentOrigin = Database['knowledge']['Enums']['document_origin'];
export type KnowledgeDocumentStatus = Database['knowledge']['Enums']['document_status'];

export interface KnowledgeDocumentItem {
    id: string;
    site_domain: string;
    path: string;
    origin: KnowledgeDocumentOrigin;
    knowledge_type: Database['knowledge']['Enums']['document_knowledge_type'];
    status: KnowledgeDocumentStatus;
    title: string | null;
    source_url: string | null;
    tags: string[];
    current_snapshot_id: string | null;
    created_at: string;
    updated_at: string;
    last_processed_at: string | null;
    /** True when the current snapshot keeps an original source binary (PDF/DOCX/…). */
    has_original: boolean;
}

/** A doc is pending re-ingestion when it changed after its last ingest. */
export function isDocumentPending(doc: KnowledgeDocumentItem): boolean {
    return doc.last_processed_at === null || doc.updated_at > doc.last_processed_at;
}

export interface KnowledgeReingestRun {
    id: string;
    site_domain: string;
    status: 'running' | 'succeeded' | 'failed';
    processed: number;
    total: number;
    error: string | null;
    started_at: string;
    finished_at: string | null;
}

export interface KnowledgeDocumentBody {
    document_id: string;
    path: string;
    title: string | null;
    markdown: string;
}

export interface CreateKnowledgeDocumentData {
    workspace_id: string;
    name: string;
    markdown: string;
}

export interface UploadKnowledgeDocumentData {
    workspace_id: string;
    name: string;
    file: File;
}

export interface UpdateKnowledgeDocumentData {
    workspace_id: string;
    name: string;
    markdown: string;
}

/** Document metadata as returned by the admin API (row + current snapshot). */
export interface KnowledgeDocumentMutationResponse {
    id: string;
    site_domain: string;
    path: string;
    origin: KnowledgeDocumentOrigin;
    knowledge_type: string;
    status: string;
    title: string | null;
    source_url: string | null;
    tags: string[];
    current_snapshot_id: string | null;
    current_version: number | null;
    updated_at: string | null;
}

export class KnowledgeDocumentsError extends Error {
    status: number | null;

    constructor(message: string, status: number | null = null) {
        super(message);
        this.name = 'KnowledgeDocumentsError';
        this.status = status;
    }
}

const fail: ServiceErrorFactory = (message, status = null) =>
    new KnowledgeDocumentsError(message, status);

function mapRowToItem(row: DocumentRow, hasOriginal: boolean): KnowledgeDocumentItem {
    return {
        id: row.id,
        site_domain: row.site_domain,
        path: row.path,
        origin: row.origin,
        knowledge_type: row.knowledge_type,
        status: row.status,
        title: row.title,
        source_url: row.source_url,
        tags: row.tags ?? [],
        current_snapshot_id: row.current_snapshot_id,
        created_at: row.created_at,
        updated_at: row.updated_at,
        last_processed_at: row.last_processed_at,
        has_original: hasOriginal,
    };
}

/**
 * Fetch all non-deleted managed knowledge documents for a workspaceId, ordered by path.
 *
 * `knowledge.documents` is shared with the scraping pipeline (origin=scrape
 * rows shown on the Website corpus page). This page only manages uploads /
 * feedback / unknown origins plus documents migrated out of Google Drive
 * (identified by their tag) — same scope as the backend store.
 */
export async function fetchKnowledgeDocuments(
    workspaceId: string
): Promise<KnowledgeDocumentItem[]> {
    const { data, error } = await supabase
        .schema('knowledge')
        .from('documents')
        .select(
            'id, site_domain, path, origin, knowledge_type, status, title, source_url, tags, current_snapshot_id, is_deleted, created_at, updated_at, last_processed_at'
        )
        .eq('workspace_id', workspaceId)
        .eq('is_deleted', false)
        .or('origin.in.(upload,feedback,unknown),tags.cs.{migrated-from-gdrive}')
        .order('path', { ascending: true });

    if (error) {
        logger.error('Error fetching knowledge documents:', error);
        throw new KnowledgeDocumentsError('Failed to fetch knowledge documents');
    }

    const rows = data ?? [];
    const snapshotIds = rows
        .map((row) => row.current_snapshot_id)
        .filter((id): id is string => id !== null);
    const withOriginal = await fetchSnapshotsWithOriginal(snapshotIds);
    return rows.map((row) =>
        mapRowToItem(
            row,
            row.current_snapshot_id !== null && withOriginal.has(row.current_snapshot_id)
        )
    );
}

/**
 * Return the subset of the given snapshot ids whose `original_ref` is set — i.e.
 * documents that keep an original source binary. Authenticated users have
 * RLS-scoped SELECT on `knowledge.document_snapshots`.
 */
async function fetchSnapshotsWithOriginal(snapshotIds: string[]): Promise<Set<string>> {
    if (snapshotIds.length === 0) return new Set();
    const { data, error } = await supabase
        .schema('knowledge')
        .from('document_snapshots')
        .select('id, original_ref')
        .in('id', snapshotIds)
        .not('original_ref', 'is', null);
    if (error) {
        // Non-fatal: the list still renders, just without "view original" affordances.
        logger.error('Error fetching knowledge document snapshots:', error);
        return new Set();
    }
    return new Set((data ?? []).map((snapshot) => snapshot.id));
}

// ---------------------------------------------------------------------------
// Admin API (body reads + all writes)
// ---------------------------------------------------------------------------

async function adminFetch<T>(path: string, init: RequestInit): Promise<T> {
    const baseUrl = getAdminApiBaseUrl(fail);
    const requestUrl = buildApiUrl(baseUrl, path);
    const accessToken = await getSupabaseAccessToken(fail);

    let response: Response;
    try {
        response = await fetch(requestUrl.toString(), {
            ...init,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                // The browser sets multipart Content-Type itself (it carries the
                // boundary); forcing JSON here would corrupt a FormData body.
                ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
                ...init.headers,
            },
        });
    } catch (err) {
        // A browser reports every unreachable host, blocked preflight and
        // CORS-less error response as the same opaque "Failed to fetch", so name
        // what we were calling — that is the only clue the user can act on.
        const message = err instanceof Error ? err.message : 'Unknown network error';
        logger.error('Admin API request failed', { url: requestUrl.toString(), message });
        throw new KnowledgeDocumentsError(
            `Could not reach the admin API at ${baseUrl} (${message}). Check that it is running.`
        );
    }

    if (!response.ok) {
        const detail = await parseErrorDetail(response);
        logger.error('Admin API returned an error', {
            url: requestUrl.toString(),
            status: response.status,
            detail,
        });
        // The server's `detail` is written for this reader; a status-code prefix
        // in front of it only buries the sentence that says what to do.
        throw new KnowledgeDocumentsError(
            detail || `The admin API returned ${response.status} ${response.statusText}.`,
            response.status
        );
    }

    if (response.status === 204) {
        return undefined as T;
    }

    try {
        return (await response.json()) as T;
    } catch {
        throw new KnowledgeDocumentsError('Failed to parse knowledge documents response');
    }
}

/**
 * Fetch the current markdown body of a document (GCS via admin API).
 */
export async function fetchKnowledgeDocumentBody(
    id: string,
    workspaceId: string
): Promise<KnowledgeDocumentBody> {
    return adminFetch<KnowledgeDocumentBody>(
        `/admin/knowledge/documents/${id}/body?workspace_id=${encodeURIComponent(workspaceId)}`,
        {
            method: 'GET',
        }
    );
}

/**
 * Fetch the original source binary (PDF/DOCX/…) kept for a document and return
 * an object URL + filename. The caller opens it and revokes the URL when done.
 * The binary is proxied through the staff-gated admin API (not a public GCS URL).
 */
export async function fetchKnowledgeDocumentOriginal(
    id: string,
    workspaceId: string
): Promise<{ url: string; filename: string }> {
    const baseUrl = getAdminApiBaseUrl(fail);
    const requestUrl = buildApiUrl(
        baseUrl,
        `/admin/knowledge/documents/${id}/original?workspace_id=${encodeURIComponent(workspaceId)}`
    );
    const accessToken = await getSupabaseAccessToken(fail);

    let response: Response;
    try {
        response = await fetch(requestUrl.toString(), {
            method: 'GET',
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown network error';
        throw new KnowledgeDocumentsError(`Network error: ${message}`);
    }
    if (!response.ok) {
        const detail = await parseErrorDetail(response);
        throw new KnowledgeDocumentsError(
            `Failed to load original document (${response.status})${detail ? `: ${detail}` : ''}`,
            response.status
        );
    }

    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1]?.trim() || 'original';
    const blob = await response.blob();
    return { url: URL.createObjectURL(blob), filename };
}

/**
 * Create an uploaded knowledge document with its v1 snapshot. The storage path
 * is derived server-side from a slug of the name.
 */
export async function createKnowledgeDocument(
    data: CreateKnowledgeDocumentData
): Promise<KnowledgeDocumentMutationResponse> {
    return adminFetch<KnowledgeDocumentMutationResponse>('/admin/knowledge/documents', {
        method: 'POST',
        body: JSON.stringify({
            workspace_id: data.workspace_id,
            name: data.name.trim(),
            markdown: data.markdown,
        }),
    });
}

// Staged uploads survive a failed extraction, so remember which files already
// reached GCS. Retrying a 50MB deck because the parse 502'd should not re-send
// 50MB. Keyed on the file's identity; cleared once the document exists, or when
// the server says the staged object is gone.
const STAGED_UPLOADS_KEY = 'rose.knowledge.stagedUploads';

interface StagedUpload {
    upload_ref: string;
    staged_at: number;
}

function stagedUploadKey(workspaceId: string, file: File): string {
    return `${workspaceId}:${file.name}:${file.size}:${file.lastModified}`;
}

function readStagedUploads(): Record<string, StagedUpload> {
    try {
        return JSON.parse(sessionStorage.getItem(STAGED_UPLOADS_KEY) ?? '{}');
    } catch {
        return {};
    }
}

function writeStagedUpload(key: string, value: StagedUpload | null): void {
    try {
        const all = readStagedUploads();
        if (value) {
            all[key] = value;
        } else {
            delete all[key];
        }
        sessionStorage.setItem(STAGED_UPLOADS_KEY, JSON.stringify(all));
    } catch {
        // A full or unavailable sessionStorage only costs us the re-upload.
        logger.warn('Could not persist staged upload state');
    }
}

interface UploadUrlResponse {
    upload_url: string;
    upload_ref: string;
    content_type: string;
    expires_in_seconds: number;
}

/**
 * PUT one file straight to GCS on a signed URL, reporting progress.
 *
 * XMLHttpRequest rather than fetch: only XHR reports upload progress, and a
 * 50MB file on a slow connection needs a bar rather than a spinner. No
 * Authorization header — the signature in the URL is the credential, and an
 * extra one would make GCS ignore it. `Content-Type` and the length range are
 * both signed, so they have to go out exactly as the server issued them.
 */
function putToSignedUrl(
    signed: UploadUrlResponse,
    file: File,
    onProgress?: (percent: number) => void
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('PUT', signed.upload_url);
        request.setRequestHeader('Content-Type', signed.content_type);
        request.setRequestHeader('x-goog-content-length-range', `1,${MAX_UPLOAD_BYTES}`);

        if (onProgress) {
            request.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable && event.total > 0) {
                    onProgress(Math.round((event.loaded / event.total) * 100));
                }
            });
        }
        request.addEventListener('error', () => {
            logger.error('Signed upload PUT failed', { file: file.name, size: file.size });
            reject(
                new KnowledgeDocumentsError(
                    'Could not send the file to storage. Check your connection and try again.'
                )
            );
        });
        request.addEventListener('abort', () =>
            reject(new KnowledgeDocumentsError('Upload cancelled'))
        );
        request.addEventListener('load', () => {
            if (request.status >= 200 && request.status < 300) {
                resolve();
                return;
            }
            // GCS answers with an XML <Message>; it names the real cause
            // (expired signature, size range, missing CORS rule).
            const gcsMessage = request.responseText.match(/<Message>([^<]+)<\/Message>/)?.[1];
            logger.error('Signed upload PUT rejected', {
                file: file.name,
                status: request.status,
                body: request.responseText.slice(0, 500),
            });
            reject(
                new KnowledgeDocumentsError(
                    `Storage rejected the upload (${request.status})${gcsMessage ? `: ${gcsMessage}` : ''}`,
                    request.status
                )
            );
        });
        request.send(file);
    });
}

/**
 * Create a knowledge document from an uploaded PDF/DOCX/PPTX.
 *
 * Three steps, because the bytes never travel through the admin API: Cloud Run
 * caps HTTP/1 request bodies at 32 MiB and the service runs uvicorn, which has
 * no HTTP/2. The API validates and hands back a signed URL, the browser PUTs to
 * GCS, then the API extracts the markdown and keeps the original for reference.
 *
 * `onProgress` reports 0-100 for the bytes sent; extraction runs after that and
 * has no progress to report.
 */
export async function uploadKnowledgeDocument(
    data: UploadKnowledgeDocumentData,
    onProgress?: (percent: number) => void
): Promise<KnowledgeDocumentMutationResponse> {
    const stagedKey = stagedUploadKey(data.workspace_id, data.file);
    let uploadRef = readStagedUploads()[stagedKey]?.upload_ref;

    if (!uploadRef) {
        const signed = await adminFetch<UploadUrlResponse>(
            '/admin/knowledge/documents/upload-url',
            {
                method: 'POST',
                body: JSON.stringify({
                    workspace_id: data.workspace_id,
                    filename: data.file.name,
                    size_bytes: data.file.size,
                    name: data.name.trim(),
                }),
            }
        );
        await putToSignedUrl(signed, data.file, onProgress);
        onProgress?.(100);
        uploadRef = signed.upload_ref;
        writeStagedUpload(stagedKey, { upload_ref: uploadRef, staged_at: Date.now() });
    } else {
        // Already in the bucket from an earlier attempt; nothing to send.
        onProgress?.(100);
    }

    try {
        const document = await adminFetch<KnowledgeDocumentMutationResponse>(
            '/admin/knowledge/documents/from-upload',
            {
                method: 'POST',
                body: JSON.stringify({
                    workspace_id: data.workspace_id,
                    upload_ref: uploadRef,
                    filename: data.file.name,
                    name: data.name.trim(),
                }),
            }
        );
        writeStagedUpload(stagedKey, null);
        return document;
    } catch (error) {
        // 404 means the staged object was reaped or never landed: drop the ref so
        // the next attempt uploads afresh rather than retrying a dead one.
        if (error instanceof KnowledgeDocumentsError && error.status === 404) {
            writeStagedUpload(stagedKey, null);
        }
        throw error;
    }
}

/**
 * Write a new body snapshot (and name) for a document.
 */
export async function updateKnowledgeDocument(
    id: string,
    data: UpdateKnowledgeDocumentData
): Promise<KnowledgeDocumentMutationResponse> {
    return adminFetch<KnowledgeDocumentMutationResponse>(`/admin/knowledge/documents/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
            workspace_id: data.workspace_id,
            name: data.name.trim(),
            markdown: data.markdown,
        }),
    });
}

/**
 * Soft-delete a knowledge document.
 */
export async function deleteKnowledgeDocument(id: string, workspaceId: string): Promise<void> {
    await adminFetch<undefined>(
        `/admin/knowledge/documents/${id}?workspace_id=${encodeURIComponent(workspaceId)}`,
        { method: 'DELETE' }
    );
}

export interface ReingestKnowledgeResponse {
    site_domain: string;
    job: string;
    status: string;
    operation_id: string | null;
    status_url: string | null;
    progress_owner: KnowledgeProgressOwner;
}

type ReingestKnowledgeApiResponse = Omit<ReingestKnowledgeResponse, 'progress_owner'>;

/**
 * Queue an agent retrain for a workspaceId: re-ingest all its managed knowledge into
 * LightRAG now.
 *
 * Backoffice edits only write Supabase + GCS; re-ingestion normally waits for
 * the scheduled sync tick. This triggers the document-loader Cloud Run Job in
 * DOCS_ONLY mode so the changes are picked up immediately. Keyed on the workspaceId
 * so it works even when the tenant has no documents open.
 */
export type KnowledgeReingestKind = 'documents' | 'faqs';

export async function reingestKnowledgeDomain(
    workspaceId: string,
    kind: KnowledgeReingestKind = 'documents'
): Promise<ReingestKnowledgeResponse> {
    const scope = `knowledge-reingest:${workspaceId.toLowerCase()}:${kind}`;
    const requestKey = pendingRequestKey(scope);
    let response: ReingestKnowledgeApiResponse;
    try {
        response = await adminFetch<ReingestKnowledgeApiResponse>('/admin/knowledge/reingest', {
            method: 'POST',
            headers: { 'Idempotency-Key': requestKey },
            body: JSON.stringify({ workspace_id: workspaceId, kind }),
        });
        clearPendingRequestKey(scope);
    } catch (error) {
        if (!shouldRetainRequestKey(error)) clearPendingRequestKey(scope);
        throw error;
    }
    const operationId = response.operation_id ?? null;
    return {
        ...response,
        operation_id: operationId,
        status_url: response.status_url ?? null,
        progress_owner: operationId ? 'knowledge_operation' : 'legacy',
    };
}

/**
 * Fetch the most recent re-ingest (agent retrain) run of a given kind for a
 * workspaceId, for the live progress bar. Reads knowledge.reingest_runs directly
 * (RLS-scoped).
 */
export async function fetchLatestReingestRun(
    workspaceId: string,
    kind: KnowledgeReingestKind = 'documents'
): Promise<KnowledgeReingestRun | null> {
    const { data, error } = await supabase
        .schema('knowledge')
        .from('reingest_runs')
        .select('id, site_domain, status, processed, total, error, started_at, finished_at')
        .eq('workspace_id', workspaceId)
        .eq('kind', kind)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) {
        logger.error('Error fetching reingest run:', error);
        return null;
    }
    if (!data) return null;
    return {
        id: data.id,
        site_domain: data.site_domain,
        status: data.status as KnowledgeReingestRun['status'],
        processed: data.processed,
        total: data.total,
        error: data.error,
        started_at: data.started_at,
        finished_at: data.finished_at,
    };
}
